import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, IconButton, Paper, TextField, Typography, List, ListItem, ListItemText, Avatar, Tooltip } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import ChatSettingsPanel from '../ChatSettingsPanel';
import './ChatWidget.css';
import * as chatApi from '../../api/chat';
import { useAuth } from '../../contexts/AuthContext';
import { countActivePaidFeatures } from '../../utils/premiumAccess';
import { formatChatMarkdown, hasLatexDelimiters } from './formatChatMarkdown';

export { formatChatMarkdown } from './formatChatMarkdown';

export const isConfirmationMessage = (text: string) => {
  const explicitConfirmation = /\b(?:search the site|search site|show related pages|show related|search for me|yes,? search|yes,? show|ok,? search|please search|please do|do it|go ahead|search for that|find it|show me|please show)\b/i.test(text);
  const shortConfirmation = /^\s*(?:yes|y|ok|sure|please|affirmative|please do|do it)\s*[,.!]?\s*$/i.test(text);
  return explicitConfirmation || (shortConfirmation && text.length < 40);
};

declare global {
  interface Window {
    MathJax: any;
  }
}

const RagSourceRenderer: React.FC<{ source: any }> = ({ source }) => {
  const html = formatChatMarkdown(String(source.text || ''));
  return <div style={{ marginTop: 8 }} dangerouslySetInnerHTML={{ __html: html }} />;
};

let mathJaxLoading: Promise<void> | null = null;

const loadMathJax = (): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.MathJax && typeof window.MathJax.typesetPromise === 'function') {
    return Promise.resolve();
  }
  if (mathJaxLoading) return mathJaxLoading;

  if (!(window as any).MathJax) {
    (window as any).MathJax = {
      tex: {
        inlineMath: [['$', '$'], ['\\(', '\\)']],
        displayMath: [['$$', '$$'], ['\\[', '\\]']],
        processEscapes: true,
      },
      startup: { typeset: false },
    };
  }

  mathJaxLoading = new Promise((resolve, reject) => {
    const existing = document.getElementById('einstein-mathjax');
    const onReady = () => resolve();
    const onError = () => {
      mathJaxLoading = null;
      reject(new Error('MathJax failed to load'));
    };

    if (existing) {
      existing.addEventListener('load', onReady);
      existing.addEventListener('error', onError);
      return;
    }

    const script = document.createElement('script');
    script.id = 'einstein-mathjax';
    script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js';
    script.async = true;
    script.onload = onReady;
    script.onerror = onError;
    document.head.appendChild(script);
  });

  return mathJaxLoading;
};

const ChatWidget: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const activePaidFeatureCount = countActivePaidFeatures(user?.paidFeatures, user?.paidFeatureExpiries);
  const hasChatAccess = user?.role === 'admin' || activePaidFeatureCount >= 2;
  
  // Generate or retrieve a persistent user ID for unauthenticated users
  const [userId] = useState(() => {
    const key = 'einstein-chat-user-id';
    let id = localStorage.getItem(key);
    if (!id) {
      id = `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem(key, id);
    }
    return id;
  });

  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Array<{ from: 'user' | 'bot'; text?: string; html?: string; rag?: any[] }>>([]);
  const [sending, setSending] = useState(false);
  const [liveStatus, setLiveStatus] = useState('Einstein is ready to help');
  const [conversationId] = useState(`conv-${Date.now()}`);
  const listRef = useRef<HTMLUListElement | null>(null);
  const liveTimeoutRef = useRef<number | null>(null);
  const soundPlayedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const floatingToolsRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{ x: number; y: number; left: number; top: number; width: number; height: number; pointerId: number } | null>(null);
  const dragInputRef = useRef<'pointer' | 'touch' | null>(null);
  const [floatingToolsPosition, setFloatingToolsPosition] = useState<{ left: number; top: number } | null>(null);

  const startFloatingToolsDrag = useCallback((
    x: number,
    y: number,
    pointerId: number,
    input: 'pointer' | 'touch',
    target: EventTarget | null
  ) => {
    if (!floatingToolsRef.current) return;
    const targetElement = target instanceof Element ? target : null;
    const isDragHandle = Boolean(targetElement?.closest('.floating-tools-drag-handle'));
    const isChatHeader = Boolean(targetElement?.closest('.chat-header'));
    if (!isDragHandle && !isChatHeader) return;
    if (isChatHeader && targetElement?.closest('button')) return;

    const bounds = floatingToolsRef.current.getBoundingClientRect();
    dragStartRef.current = {
      x,
      y,
      left: bounds.left,
      top: bounds.top,
      width: bounds.width,
      height: bounds.height,
      pointerId,
    };
    dragInputRef.current = input;
    setFloatingToolsPosition({ left: bounds.left, top: bounds.top });
    if (input === 'pointer' && targetElement && 'setPointerCapture' in targetElement) {
      try {
        targetElement.setPointerCapture(pointerId);
      } catch {
        // Document-level listeners keep the drag active if pointer capture is unavailable.
      }
    }
  }, []);

  const startPointerDrag = useCallback((event: PointerEvent) => {
    if (dragInputRef.current === 'touch') return;
    startFloatingToolsDrag(event.clientX, event.clientY, event.pointerId, 'pointer', event.target);
    if (dragInputRef.current === 'pointer' && event.cancelable) event.preventDefault();
  }, [startFloatingToolsDrag]);

  const startTouchDrag = useCallback((event: TouchEvent) => {
    if (dragInputRef.current === 'pointer') return;
    const touch = event.changedTouches[0];
    if (!touch) return;
    startFloatingToolsDrag(touch.clientX, touch.clientY, touch.identifier, 'touch', event.target);
    if (dragInputRef.current === 'touch' && event.cancelable) event.preventDefault();
  }, [startFloatingToolsDrag]);

  const updateFloatingToolsPosition = useCallback((x: number, y: number, pointerId: number | string) => {
    const dragStart = dragStartRef.current;
    if (!dragStart || dragStart.pointerId !== pointerId) return;
    const padding = 8;
    const left = Math.min(
      Math.max(padding, dragStart.left + x - dragStart.x),
      Math.max(padding, window.innerWidth - dragStart.width - padding)
    );
    const top = Math.min(
      Math.max(padding, dragStart.top + y - dragStart.y),
      Math.max(padding, window.innerHeight - dragStart.height - padding)
    );
    setFloatingToolsPosition({ left, top });
  }, []);

  const dragFloatingTools = useCallback((event: PointerEvent) => {
    if (dragInputRef.current !== 'pointer') return;
    updateFloatingToolsPosition(event.clientX, event.clientY, event.pointerId);
  }, [updateFloatingToolsPosition]);

  const dragFloatingToolsWithTouch = useCallback((event: TouchEvent) => {
    const dragStart = dragStartRef.current;
    if (dragInputRef.current !== 'touch' || !dragStart) return;
    const touch = Array.from(event.changedTouches).find(({ identifier }) => identifier === dragStart.pointerId);
    if (!touch) return;
    event.preventDefault();
    updateFloatingToolsPosition(touch.clientX, touch.clientY, touch.identifier);
  }, [updateFloatingToolsPosition]);

  const stopDraggingFloatingTools = useCallback((event: PointerEvent) => {
    if (dragInputRef.current !== 'pointer') return;
    if (event.type === 'pointerup') {
      updateFloatingToolsPosition(event.clientX, event.clientY, event.pointerId);
    }
    dragStartRef.current = null;
    dragInputRef.current = null;
  }, [updateFloatingToolsPosition]);

  const stopTouchDraggingFloatingTools = useCallback((event: TouchEvent) => {
    const dragStart = dragStartRef.current;
    if (dragInputRef.current !== 'touch' || !dragStart) return;
    const touch = Array.from(event.changedTouches).find(({ identifier }) => identifier === dragStart.pointerId);
    if (touch) {
      if (event.type === 'touchend') {
        updateFloatingToolsPosition(touch.clientX, touch.clientY, touch.identifier);
      }
      dragStartRef.current = null;
      dragInputRef.current = null;
    }
  }, [updateFloatingToolsPosition]);

  useEffect(() => {
    document.addEventListener('pointerdown', startPointerDrag, true);
    document.addEventListener('pointermove', dragFloatingTools, true);
    document.addEventListener('pointerup', stopDraggingFloatingTools, true);
    document.addEventListener('pointercancel', stopDraggingFloatingTools, true);
    document.addEventListener('touchstart', startTouchDrag, { capture: true, passive: false });
    document.addEventListener('touchmove', dragFloatingToolsWithTouch, { capture: true, passive: false });
    document.addEventListener('touchend', stopTouchDraggingFloatingTools, true);
    document.addEventListener('touchcancel', stopTouchDraggingFloatingTools, true);
    return () => {
      document.removeEventListener('pointerdown', startPointerDrag, true);
      document.removeEventListener('pointermove', dragFloatingTools, true);
      document.removeEventListener('pointerup', stopDraggingFloatingTools, true);
      document.removeEventListener('pointercancel', stopDraggingFloatingTools, true);
      document.removeEventListener('touchstart', startTouchDrag, true);
      document.removeEventListener('touchmove', dragFloatingToolsWithTouch, true);
      document.removeEventListener('touchend', stopTouchDraggingFloatingTools, true);
      document.removeEventListener('touchcancel', stopTouchDraggingFloatingTools, true);
    };
  }, [dragFloatingTools, dragFloatingToolsWithTouch, startPointerDrag, startTouchDrag, stopDraggingFloatingTools, stopTouchDraggingFloatingTools]);

  const renderFloatingToolsDragHandle = () => (
    <Tooltip title="Drag to move chatbot and contact" arrow disableTouchListener>
      <IconButton
        className="floating-tools-drag-handle"
        aria-label="Move chatbot and contact widgets"
        size="small"
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const renderContactWidget = () => (
    <a className="chat-contact-widget" href="/helpdesk" aria-label="Open technical support helpdesk">
      <ContactSupportIcon fontSize="small" />
      <span>Helpdesk</span>
    </a>
  );

  // Play notification sound on mount and when user logs in or page refreshes
  useEffect(() => {
    if (isAuthenticated && !soundPlayedRef.current) {
      soundPlayedRef.current = true;
      // Play a pleasant notification sound
      const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext;
      if (typeof AudioContextConstructor !== 'function') return;
      const audioContext = new AudioContextConstructor();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Create a pleasant chime sound: two notes
      oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } else if (!isAuthenticated) {
      // Reset the flag when user logs out
      soundPlayedRef.current = false;
    }
  }, [isAuthenticated]);

  // Close and reset chatbot when user logs out
  useEffect(() => {
    if (isAuthenticated) {
      // Prepare Einstein immediately after login (ensure welcome message exists)
      // Do NOT open the widget automatically; wait for user to open it.
      setMessages(prev => {
        if (!prev || prev.length === 0) {
          return [{ from: 'bot', text: 'Hi! I am Einstein — I can help answer your questions about the A3 website, our projects, and research and any of your queries. Ask me anything.' }];
        }
        return prev;
      });
      setLiveStatus('Einstein is ready to help');
    } else {
      setOpen(false);
      setMessages([]);
      setInput('');
      setSettingsOpen(false);
    }
  }, [isAuthenticated]);

  // Lazy-load MathJax only when a message contains LaTeX, then typeset the transcript.
  useEffect(() => {
    const shouldTypeset = messages.some((m) => hasLatexDelimiters(String(m.html || m.text || '')));
    if (!shouldTypeset) return;

    let cancelled = false;
    loadMathJax()
      .then(() => {
        if (cancelled) return;
        const mj = window.MathJax;
        if (typeof mj?.typesetPromise !== 'function') return;
        const root = listRef.current;
        if (typeof mj.typesetClear === 'function' && root) {
          mj.typesetClear([root]);
        }
        return mj.typesetPromise(root ? [root] : undefined);
      })
      .catch((err: any) => {
        // eslint-disable-next-line no-console
        console.warn('[MathJax] failed to typeset', err);
      });

    return () => {
      cancelled = true;
    };
  }, [messages]);

  // Initialize welcome message on component mount
  useEffect(() => {
    setMessages(prev => {
      if (prev.length === 0) {
        return [{ from: 'bot', text: 'Hi! I am Einstein — I can help answer your questions about the A3 website, our projects, and research and any of your queries. Ask me anything.' }];
      }
      return prev;
    });
    setLiveStatus('Einstein is ready to help');
    return () => {
      if (liveTimeoutRef.current) {
        window.clearTimeout(liveTimeoutRef.current);
        liveTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // scroll to bottom when messages change
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Focus the input when the widget is opened by the user
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      try {
        inputRef.current?.focus();
      } catch (e) {
        // ignore
      }
    }, 80);
    return () => window.clearTimeout(t);
  }, [open]);

  // When a new bot message arrives and the widget is open, focus the input
  useEffect(() => {
    if (!open || !messages || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last && last.from === 'bot') {
      const t = window.setTimeout(() => {
        try {
          inputRef.current?.focus();
        } catch (e) {
          // ignore
        }
      }, 120);
      return () => window.clearTimeout(t);
    }
  }, [messages, open]);

  // Helper to format source name from RAG context without exposing file paths
  const formatSourceName = (source: string, text?: string) => {
    if (!source) return 'Source';
    // If project id, try to extract a human title from the snippet
    if (source.startsWith('project:')) {
      const lines = (text || '').split('\n').map(l => l.trim()).filter(Boolean);
      const title = lines.find(l => l.length > 25 && !/[:{}()[\]]/.test(l));
      if (title) return title.substring(0, 80);
      return 'Project reference';
    }

    // Remove common prefixes then take basename (no folder paths)
    let name = source.replace(/^(file:|data:|project:)/, '');
    name = name.replace(/\\/g, '/');
    const parts = name.split('/');
    name = parts[parts.length - 1] || parts[0] || name;
    // Remove file extension
    name = name.replace(/\.[a-z0-9]{1,6}$/i, '');
    // Convert delimiters to spaces and title-case a bit
    name = name.replace(/[-_]+/g, ' ').trim();
    if (name.length === 0) return 'Source';
    return name.length > 60 ? name.substring(0, 60) + '...' : name;
  };

  // Create a short, user-friendly snippet for RAG excerpts
  const formatSnippet = (text?: string, max = 180) => {
    if (!text) return '';
    const cleaned = text.replace(/\s+/g, ' ').trim();
    // Prefer a full sentence-like fragment
    const sentences = cleaned.split(/(?<=[.?!])\s+/);
    const candidate = sentences.find(s => s.length > 40 && !/[:{}=<>]/.test(s));
    const out = (candidate || cleaned).substring(0, max).trim();
    return out.length < cleaned.length ? out + '…' : out;
  };

  const handleSend = async () => {
    if (!hasChatAccess) return;
    const text = input.trim();
    if (!text) return;
    setMessages(prev => [...prev, { from: 'user', text }]);
    setInput('');
    setSending(true);
    // announce typing to screen readers
    setLiveStatus('Einstein is typing');
    // clear any previous reset timer
    if (liveTimeoutRef.current) {
      window.clearTimeout(liveTimeoutRef.current);
      liveTimeoutRef.current = null;
    }
    try {
      // If this message looks like a confirmation ("yes, search the site"),
      // send the previous user message to the backend so RAG can search for it
      const lastUserMessage = messages.slice().reverse().find(m => m.from === 'user')?.text || undefined;
      const isConfirmationLocal = isConfirmationMessage(text);
      const res = await chatApi.sendMessage(text, conversationId, userId, isConfirmationLocal ? lastUserMessage : undefined);
      
      // Debug logging
      console.log('[ChatWidget] Chat response:', res);
      
      // If backend returns structured rag data, capture it
      const replyText = (res && (res.reply || res.message || res.data)) || '';
      const rag = (res && res.rag) || (res && res.contexts) || null;
      
      if (!replyText) {
        console.warn('[ChatWidget] No reply text in response:', res);
      }
      
      // Format markdown synchronously
      const html = formatChatMarkdown(String(replyText));
      setMessages(prev => [...prev, { from: 'bot', text: String(replyText), html, rag }]);

      // update live region to indicate response arrived
      setLiveStatus('Einstein has responded');
      // after a short delay, reset live region to ready
      liveTimeoutRef.current = window.setTimeout(() => {
        setLiveStatus('Einstein is ready to help');
        liveTimeoutRef.current = null;
      }, 3000);
    } catch (err) {
      console.error('[ChatWidget] handleSend error:', err);
      // Format error message with markdown formatter
      const errorHtml = formatChatMarkdown('There was an error contacting the chat service. Try again later.');
      setMessages(prev => [...prev, { from: 'bot', text: 'There was an error contacting the chat service. Try again later.', html: errorHtml }]);
      setLiveStatus('Einstein is available');
      // reset after short delay
      liveTimeoutRef.current = window.setTimeout(() => {
        setLiveStatus('Einstein is ready to help');
        liveTimeoutRef.current = null;
      }, 3000);
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div
      ref={floatingToolsRef}
      className={`floating-tools-root${floatingToolsPosition ? ' floating-tools-root--positioned' : ''}`}
      style={floatingToolsPosition ? { left: floatingToolsPosition.left, top: floatingToolsPosition.top } : undefined}
    >
      {!open && <Box className="floating-tools-closed-handle">{renderFloatingToolsDragHandle()}</Box>}
      <div className="chat-toggle-root">
        {!open && isAuthenticated && (
          <Tooltip title={hasChatAccess ? 'Chat with Einstein' : 'Subscribe to two paid features to unlock Einstein'} arrow>
            <IconButton 
              color="primary" 
              onClick={() => setOpen(true)} 
              size="large" 
              aria-label="Open Einstein chat"
              sx={{
                position: 'relative',
                animation: 'pulse-bounce 2s infinite',
                '@keyframes pulse-bounce': {
                  '0%': {
                    transform: 'scale(1)',
                    boxShadow: '0 0 0 0 rgba(0, 102, 255, 0.7)'
                  },
                  '50%': {
                    transform: 'scale(1.05)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                    boxShadow: '0 0 0 12px rgba(0, 102, 255, 0)'
                  }
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  width: '20px',
                  height: '20px',
                  backgroundColor: '#ff1744',
                  borderRadius: '50%',
                  animation: 'pulse-badge 2s ease-in-out infinite',
                  boxShadow: '0 0 8px rgba(255, 23, 68, 0.6)',
                  '@keyframes pulse-badge': {
                    '0%, 100%': {
                      transform: 'scale(1)',
                      opacity: 1
                    },
                    '50%': {
                      transform: 'scale(1.2)',
                      opacity: 0.8
                    }
                  }
                }
              }}
            >
              <ChatIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </div>
      {!open && renderContactWidget()}

      {open && isAuthenticated && !hasChatAccess && (
        <Paper elevation={8} className="chat-widget-container" role="dialog" aria-label="AI tutor subscription required">
          <Box className="chat-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src={process.env.PUBLIC_URL + '/logo_with_bottom_text.svg'} alt="A3Tech AI Tutor" aria-hidden />
              <Typography variant="subtitle1">Einstein</Typography>
            </Box>
            <Tooltip title="Close Einstein chat">
              <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close Einstein chat"><CloseIcon /></IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ p: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Unlock the AI Tutor</Typography>
            <Typography variant="body2" color="text.secondary">
              Subscribe to at least two paid features to use Einstein. You currently have {activePaidFeatureCount} active {activePaidFeatureCount === 1 ? 'feature' : 'features'}.
            </Typography>
            <Button component="a" href="/payment" variant="contained">Choose paid features</Button>
          </Box>
        </Paper>
      )}

      {open && isAuthenticated && hasChatAccess && (
        <Paper elevation={8} className="chat-widget-container" role="dialog" aria-label="Einstein chat widget">
          <Box className="chat-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src={process.env.PUBLIC_URL + '/logo_with_bottom_text.svg'} alt="A3Tech AI Tutor" aria-hidden />
              <Typography variant="subtitle1">Einstein</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Box className="floating-tools-open-handle">{renderFloatingToolsDragHandle()}</Box>
              <Tooltip title="Chat Settings & History">
                <IconButton size="small" onClick={() => setSettingsOpen(true)} aria-label="Open chat settings">
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close Einstein chat">
                <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close Einstein chat">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {/* Accessible live region for screen readers when widget opens */}
          <div role="status" aria-live="polite" className="sr-only">{liveStatus}</div>

          <List className="chat-messages" ref={listRef as any}>
            {messages.map((m, i) => (
              <ListItem key={i} sx={{ alignItems: 'flex-start' }}>
                <ListItemText
                  primary={m.from === 'user' ? 'You' : 'Einstein'}
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={
                    m.from === 'user' ? (
                      m.text
                    ) : (
                      <div>
                        {/* Render formatted HTML reply when available */}
                        {m.html ? (
                          <div dangerouslySetInnerHTML={{ __html: m.html }} />
                        ) : (
                          <div>{m.text}</div>
                        )}

                        {/* If RAG contexts exist, show compact source list with expandable excerpts */}
                        {m.rag && Array.isArray(m.rag) && m.rag.length > 0 && (
                            <div className="chat-related-sources">
                                            <div className="chat-related-sources-title">Related sources</div>
                            <ul style={{ paddingLeft: '1rem', marginTop: 6 }}>
                              {m.rag.slice(0, 5).map((r, idx) => (
                                <li key={idx} style={{ marginBottom: 6 }}>
                                    <details style={{ padding: '6px 0' }}>
                                      <summary className="chat-related-source">
                                        📄 {formatSourceName(r.source, r.text)}
                                        <div className="chat-related-source-snippet">{formatSnippet(String(r.text || ''))}</div>
                                      </summary>
                                      <RagSourceRenderer source={r} />
                                    </details>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )
                  }
                />
              </ListItem>
            ))}
            {sending && (
              <ListItem sx={{ alignItems: 'flex-start' }}>
                <ListItemText primary="Einstein" secondary={<em>Typing…</em>} />
              </ListItem>
            )}
          </List>

          <Box className="chat-input-row">
            <TextField
              placeholder="Type a question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } }}
              size="small"
              fullWidth
              inputRef={inputRef}
              inputProps={{ 'aria-label': 'Einstein chat input' }}
            />
            <IconButton color="primary" onClick={handleSend} disabled={sending} aria-label="Send message to Einstein">
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      )}
      {open && isAuthenticated && renderContactWidget()}

      {open && !isAuthenticated && (
        <Paper elevation={8} className="chat-widget-container" role="dialog" aria-label="Einstein chat widget - login required">
          <Box className="chat-header">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar src={process.env.PUBLIC_URL + '/logo_with_bottom_text.svg'} alt="A3Tech AI Tutor" aria-hidden />
              <Typography variant="subtitle1">Einstein</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Close Einstein chat">
                <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close Einstein chat">
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ p: 3, textAlign: 'center', minHeight: '300px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              Authentication Required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: '90%' }}>
              To use Einstein and access personalized chat features, you need to be logged in.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', width: '100%', maxWidth: '250px' }}>
              <button
                className="chat-auth-button chat-auth-button--login"
                onClick={() => window.location.href = '/login'}
              >
                Login
              </button>
              <button
                className="chat-auth-button chat-auth-button--register"
                onClick={() => window.location.href = '/signup'}
              >
                Register
              </button>
            </Box>
          </Box>
        </Paper>
      )}
      {open && !isAuthenticated && renderContactWidget()}

      {isAuthenticated && (
        <ChatSettingsPanel
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          conversationId={conversationId}
        />
      )}
    </div>,
    document.body
  );
};

export default ChatWidget;
